import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'underscore'
import axios from 'axios'
import slugify from 'slugify'
import api from '@/api'
import constants from '@/constants'
import live from '@/live'

Vue.use(Vuex)

// the answer to the permission prompt can land after the one-shot lookup in the
// `locate` action has already been answered: a slow "Allow" outruns the timeout,
// and a grant made from the padlock menu never calls back at all. Watching the
// permission re-runs the lookup when that happens, so the distances appear in
// place - the visitor never has to reload the page.
var watchingPermission = false

function watchPermission(context) {

	if (watchingPermission) return

	if (!navigator.permissions || !navigator.permissions.query) return

	watchingPermission = true

	try {

		navigator.permissions.query({ name: 'geolocation' }).then(function(status) {

			status.addEventListener('change', function() {

				if (status.state !== 'granted') return

				if (context.state.position) return

				// clear whatever the one-shot latched so `locate` runs again
				context.commit('positionState', 'idle')
				context.dispatch('locate')

			})

		}).catch(function() {

			// permission state isn't observable here (Safari) - the one-shot lookup
			// and its retry are all we get

		})

	} catch (e) {

		// query() throws on an unsupported permission name in older browsers

	}

}

export default new Vuex.Store({

	modules: {
	},

	state: {
		locale: 'en',
		guide: {},
		categories: [],
		groups: [],
		texts: {},
		locations: {},
		dictionary: [],
		items: [],
		terms: [],
		category: 0,
		mobileOnly: 0,
		group: 0,
		groupFocus: 0,
		item: 0,
		location: 0,
		key: '',
		primary: 0,
		base: '',
		position: false,
		positionState: 'idle',
		drives: {}
	},

	getters: {

		locale: function(state) {

			return state.locale

		},

		guide: function(state) {

			return state.guide

		},

		mobileOnly: function(state) {

			return state.mobileOnly

		},

		categories: function(state) {

			return state.categories

		},

		'categories/find': function(state) {
		
			return function(id) {
		
				return _.findWhere(state.categories, {
					id: parseInt(id)
				})
		
			}
		
		},

		groups: function(state) {

			return state.groups

		},

		'groups/find': function(state) {
		
			return function(id) {
		
				return _.findWhere(state.groups, {
					slug: id
				})
		
			}
		
		},

		'groups/findbyid': function(state) {
		
			return function(id) {
		
				return _.findWhere(state.groups, {
					id: id
				})
		
			}
		
		},

		items: function(state) {

			return state.items

		},

		dictionary: function(state) {

			return state.dictionary

		},

		terms: function(state) {

			return state.terms

		},

		texts: function(state) {

			return state.texts

		},

		locations: function(state) {

			return state.locations

		},

		slides: function(state) {

			return state.slides

		},

		category: function(state) {

			return state.category

		},

		group: function(state) {

			return state.group

		},

		'group/focus': function(state) {

			return state.groupFocus

		},

		item: function(state) {

			return state.item

		},

		location: function(state) {

			return state.location

		},

		key: function(state) {

			return state.key

		},

		base: function(state) {

			return state.base

		},

		position: function(state) {

			return state.position

		},

		// resolved driving distance/time per location id for the current origin;
		// the distance line displays these road miles, so the closest-first sort
		// keys off them too (see mixin/blocksort)
		drives: function(state) {

			return state.drives

		}

	},

	mutations: {

		locale: function(state, value) {

			state.locale = value

		},

		key: function(state, value) {

			state.key = value

		},

		primary: function(state, value) {

			state.primary = value

		},

		mobileOnly: function(state, value) {

			state.mobileOnly = value

		},

		guide: function(state, value) {

			Vue.set(state, 'guide', value)

		},

		category: function(state, value) {

			state.category = value

		},

		group: function(state, value) {

			state.group = value

		},

		'group/focus': function(state, value) {

			state.groupFocus = value

		},

		item: function(state, value) {

			state.item = value

		},

		location: function(state, value) {

			state.location = value

		},

		position: function(state, value) {

			state.position = value

			// origin changed - previously resolved drive distances no longer apply
			state.drives = {}

		},

		positionState: function(state, value) {

			state.positionState = value

		},

		drive: function(state, value) {

			// replace the object (not Vue.set a key) so computeds that read a
			// not-yet-present id - e.g. the closest-first sort - recompute
			var next = {}

			Object.assign(next, state.drives)

			next[value.id] = value.result

			state.drives = next

		}

	},

	actions: {

		// asks the browser for the visitor's position the first time a view
		// renders a location that has coordinates; cached for the session so the
		// permission prompt and lookup only happen once. Re-entered when the
		// permission is granted later on (see watchPermission above)
		locate: function(context) {

			if (context.state.positionState !== 'idle') return

			context.commit('positionState', 'pending')

			// an address entered in the recyclebycity.com scheduler is carried
			// over (same-origin localStorage) and takes precedence over the
			// browser geolocation prompt
			try {

				var scheduled = JSON.parse(localStorage.getItem('rbc-location'))

				if (scheduled && typeof scheduled.lat === 'number' && typeof scheduled.lng === 'number' && scheduled.time > Date.now() - 86400000) {

					context.commit('position', { lat: scheduled.lat, lng: scheduled.lng })
					context.commit('positionState', 'granted')

					return

				}

			} catch (e) {

				// ignore unreadable scheduler address

			}

			try {

				var cached = JSON.parse(sessionStorage.getItem('rbc-position'))

				if (cached && cached.time > Date.now() - 600000) {

					context.commit('position', { lat: cached.lat, lng: cached.lng })
					context.commit('positionState', 'granted')

					return

				}

			} catch (e) {

				// ignore unreadable cache

			}

			if (!navigator.geolocation) {

				context.commit('positionState', 'denied')

				return

			}

			watchPermission(context)

			navigator.geolocation.getCurrentPosition(function(result) {

				var position = {
					lat: result.coords.latitude,
					lng: result.coords.longitude
				}

				try {

					sessionStorage.setItem('rbc-position', JSON.stringify({
						lat: position.lat,
						lng: position.lng,
						time: Date.now()
					}))

				} catch (e) {

					// storage unavailable

				}

				context.commit('position', position)
				context.commit('positionState', 'granted')

			}, function(error) {

				// only an outright refusal is final. A timed-out or unavailable fix
				// goes back to idle so the next location view can try again - and so
				// a grant that arrives late isn't stuck behind a latched 'denied'
				context.commit('positionState', error && error.code === 1 ? 'denied' : 'idle')

			}, {
				// generous: browsers run this timer while the permission prompt is
				// still on screen, so a short one gives up on a visitor who is still
				// reading it
				timeout: 30000,
				maximumAge: 300000
			})

		},

		init: function(context, e) {

			context.commit('key', e.key)
			context.commit('primary', e.primary)

			axios.defaults.headers.common['rbc-guide'] = e.key

			live.init()

			live.$on('updated', function() {

				context.dispatch('load')

			})

			return context.dispatch('load')
	
		},

		load: function(context) {

			return new Promise(function(resolve) {

				var params = {}

				if (context.state.primary) params.primary = context.state.primary
					
				api.get('guide/' + context.getters['key'], params).then(function(response) {

					context.state.categories = response.categories
					Vue.set(context.state, 'guide', response.guide)
					context.state.groups = response.groups
					context.state.items = response.items
					context.state.texts = response.texts
					context.state.locations = response.locations
					context.state.base = response.base

					var terms = []
					var dictionary = []

					_.each({
						//category: 'categories',
						group: 'groups',
						item: 'items'
						//location: 'locations'
					}, function(store, key) {
		
						_.each(context.getters[store], function(item) {

							var title = slugify(item.title, {
								replacement: ' ',
								remove: /[*+~.()'"!:@]/g,
								strict: true,
								lower: true
							})
		
							terms.push({
								id: item.id,
								text: title,
								type: constants.type[key]
							})

							_.each(title.split(' '), function(part) {

								dictionary.push(part)

							})
		
							_.each(item.terms, function(term) {

								term = slugify(term, {
									replacement: ' ',
									remove: /[*+~.()'"!:@]/g,
									strict: true,
									lower: true
								})
		
								terms.push({
									id: item.id,
									text: term,
									type: constants.type[key]
								})

								_.each(term.split(' '), function(part) {
	
									dictionary.push(part)
	
								})
		
							})
		
						})
		
					})

					context.state.terms = terms
					context.state.dictionary = dictionary
		
					if (!context.state.category) context.state.category = response.categories[0].id

					resolve()

				})

			})

		}

	}

})
